/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  FormControlLabel,
  Checkbox,
  Grid,
  FormControl,
  FormGroup,
  Box,
} from '@mui/material';
import {useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import {MultiGroupSelectBox} from './GroupSelectBox';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {patchDailySchedule} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {apiErrorAlert} from '../../functions/apiErrorAlert';
import DialogPage from '../../component/DialogMessage';

/**
 * 他グループへ特定日スケジュールをコピーするダイアログ
 * @param props props
 * - isOpenDialog ダイアログ表示フラグ
 * - setIsOpenDialog ダイアログ表示フラグ 設定関数
 * - scheduleChangeHandler 特定日スケジュール更新関数
 * - dailyScheduleDataBody 特定日スケジュールのBodyデータ
 * - groupID グループID
 * - groupName グループ名
 * - groupInfo グループ情報一覧
 * @returns JSX
 */
const CopyOtherGroupDialog = (props: {
  isOpenDialog: boolean;
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  scheduleChangeHandler: () => void;
  dailyScheduleData: apiIF.res.getDailySchedule;
  groupID: string;
  groupName: string;
  groupInfo: apiIF.res.getCompositionsGroups['group-informations'];
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  /** セレクトボックスで選択したグループID */
  const [targetGroupID, setTargetGroupID] = useState<string[]>([]);
  /** チェックボックスで選択した特定日タイプ */
  const [selectType, setSelectType] = useState<
    (keyof apiIF.res.getDailySchedule)[]
  >([]);

  //フラグ
  /** 確認ダイアログのオープンフラグ */
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  /** API応答待ちフラグ */
  const [apiWait, setAPIWait] = useState(false);
  /** APIエラー時エラーメッセージ表示 */
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);

  /** 特定日タイプ項目 */
  const tempType: {
    name: keyof apiIF.res.getDailySchedule;
    label: string;
  }[] = [
    {
      name: 'weekday-schedule',
      label: trans('CopyOtherGroupDialog.tempType.weekday'),
    },
    {
      name: 'holiday-schedule',
      label: trans('CopyOtherGroupDialog.tempType.holiday'),
    },
    {
      name: 'special-day-1-schedule',
      label: trans('CopyOtherGroupDialog.tempType.specialDay1'),
    },
    {
      name: 'special-day-2-schedule',
      label: trans('CopyOtherGroupDialog.tempType.specialDay2'),
    },
  ];

  /**
   * チェックボックスで選択した特定日のスケジュールをセレクトボックスで選択したグループに
   * コピーするためのBodyデータを作成
   * @param  dailyScheduleData 特定日スケジュールのレスポンスデータ
   * @returns リクエストBodyのデータ
   */
  const copyToScheduleData = (
    dailyScheduleData: apiIF.res.getDailySchedule
  ) => {
    //Bodyデータ
    let copyData: apiIF.reqParameters.patchDailySchedule = {};
    //prettier-ignore
    for (const key of Object.keys(dailyScheduleData) as (keyof apiIF.reqParameters.patchDailySchedule)[]) {
      //選択されていない特定日の場合、Bodyデータに追加しない
      if (selectType.includes(key) === false) {
        continue;
      }
      copyData[key] = {
        'schedule': dailyScheduleData[key],
        'copy-destination': targetGroupID,
      };
    }
    return copyData;
  };

  /**
   * 特定日タイプ変更時処理
   * @param event
   */
  const handleChangeType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetName = event.target.name as keyof apiIF.res.getDailySchedule;
    if (selectType.includes(targetName)) {
      setSelectType(selectType.filter(item => item !== targetName));
    } else {
      setSelectType([...selectType, targetName]);
    }
  };

  /**
   * COPYボタン押下時処理
   */
  const handleClickCopy = async () => {
    setAPIWait(true);
    if (siteID === undefined) return;
    //コピー先を指定したBodyデータを作成
    let copyScheduleDataBody = copyToScheduleData(props.dailyScheduleData);
    try {
      await patchDailySchedule(siteID, props.groupID, copyScheduleDataBody);
      //スケジュール設定更新
      props.scheduleChangeHandler();
      handleClose();
    } catch {
      apiErrorAlert(trans);
    } finally {
      setAPIWait(false);
      setIsOpenDialog(false);
    }
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setSelectType([]);
    setTargetGroupID([]);
    props.setIsOpenDialog(false);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth="md">
        <DialogTitle id="form-dialog-title">
          {trans('CopyOtherGroupDialog.title')}
        </DialogTitle>
        <DialogContent className="schedule__copyDialog__content">
          <Stack spacing={2}>
            {/** グループの名前 */}
            <Box>{props.groupName}</Box>
            {/** 特定日の種類選択欄 */}
            <Box className="schedule__copyDialog__content__specificScheduleCheckArea">
              <FormControl>
                <FormGroup>
                  {tempType.map(info => (
                    <FormControlLabel
                      key={`selectType-${info.name}`}
                      control={
                        <Checkbox
                          checked={selectType.includes(info.name)}
                          onChange={handleChangeType}
                          name={info.name}
                        />
                      }
                      label={info.label}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>
            {/** グループの選択欄 */}
            <Box className="schedule__copyDialog__content__groupSelectArea">
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={2}>
                  {trans('CopyOtherGroupDialog.to')}
                </Grid>
                <Grid item xs={10}>
                  <MultiGroupSelectBox
                    selectedGroupID={targetGroupID}
                    setSelectedGroupID={setTargetGroupID}
                    groupInfo={props.groupInfo}
                    excludeGroupID={props.groupID}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            onClick={handleClose}
            buttonText={trans('CopyOtherGroupDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={() => setIsOpenDialog(true)}
            disabled={targetGroupID.length === 0 || selectType.length === 0}
            apiWait={apiWait}
            buttonText={trans('CopyOtherGroupDialog.buttonTxt.copy')}
          />
        </DialogActions>
      </Dialog>
      <DialogPage
        open={isOpenDialog}
        setOpen={setIsOpenDialog}
        titleString={trans('Confirm.dialogBox.title')}
        contentText={trans('Confirm.dialogBox.msg')}
        buttonNameLeft={trans('Confirm.dialogBox.buttonTxt.cancel')}
        buttonNameRight={trans('Confirm.dialogBox.buttonTxt.ok')}
        redirectPath={''}
        execFunc={handleClickCopy}
        id="mv-CopyOtherGroupDialog"
      />
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
    </>
  );
};
export default CopyOtherGroupDialog;
